import classNames from 'classnames';
import { map, intersection } from 'ramda';
import PropTypes from 'prop-types';
import { Component } from 'react';
import CheckboxEditor from '../checkbox-editor/CheckboxEditor.jsx';
import OSMHoursEditor from '../osm-hours-editor/OSMHoursEditor.jsx';
import SelectFieldEditor from '../select-field-editor/SelectFieldEditor.jsx';
import TextAreaEditor from '../text-area-editor/TextAreaEditor.jsx';
import TextFieldEditor from '../text-field-editor/TextFieldEditor.jsx';
import { POI_FIELDS } from '../constants';
import { findInvalidUrlErrors, findInvalidPhoneError, findInvalidPhoneExtensionError } from '../../views/venue/validation.js';
import { normalizeAdditionalAttributes, normalizePhoneNumber } from './info-editor-helpers.js';

import s from './InfoEditor.module.scss';

const isRestricted = poi => (poi.security ? poi.security.toLowerCase() === 'inside' : false);

const makeSelectLabels = stringArray => map(item => ({ label: item, value: item }), stringArray);

export default class InfoEditor extends Component {
    static propTypes = {
        changedFields: PropTypes.arrayOf(PropTypes.string),
        disabled: PropTypes.bool,
        onUpdatePoiFields: PropTypes.func,
        poi: PropTypes.object,
        terminals: PropTypes.arrayOf(PropTypes.string),
        terminalsToLevels: PropTypes.object,
    };

    static defaultProps = {
        changedFields: [],
        disabled: false,
        onUpdatePoiFields: () => {},
        poi: {},
        terminals: [''],
        terminalsToLevels: {},
    };

    setSecurityRestricted = restricted => {
        const security = restricted ? 'Inside' : 'Outside';
        this.props.onUpdatePoiFields({ security });
    };

    renderBanner = (title, fields = [], subtitle = '') => {
        const { changedFields } = this.props;
        const isDirty = intersection(fields, changedFields).length > 0;
        const bannerClasses = classNames(s.banner, { [s.dirty]: isDirty });
        const subBanner = subtitle ? <div className={s.subBanner}>{subtitle}</div> : null;
        return (
            <div className={s.bannerWrapper}>
                <div className={bannerClasses}>{title}</div>
                {subBanner}
            </div>
        );
    };

    onPhoneNumberChange = phone => {
        this.props.onUpdatePoiFields({ phone: normalizePhoneNumber(phone) });
    };

    onPhoneExtensionChange = phoneExt => {
        const trimmed = typeof phoneExt === 'string' ? phoneExt.trim() : phoneExt;
        this.props.onUpdatePoiFields({ phoneExt: trimmed === '' ? null : phoneExt });
    };

    render = () => {
        const { poi = {}, onUpdatePoiFields, terminals = [], terminalsToLevels = {}, disabled } = this.props;
        const levels = terminalsToLevels[poi.terminal] || [];
        const urlErrors = findInvalidUrlErrors(poi);
        const phoneError = findInvalidPhoneError(poi.phone);
        const phoneExtError = findInvalidPhoneExtensionError(poi.phoneExt);

        return (
            <div className={classNames(s.infoEditor)} data-testid={'info-editor'}>
                {this.renderBanner('LOCATION', [POI_FIELDS.TERMINAL, POI_FIELDS.FLOOR, POI_FIELDS.GATE])}
                <div className={s.controlsGrid}>
                    <SelectFieldEditor
                        label="Building"
                        selectedValue={poi.terminal}
                        creatable
                        options={makeSelectLabels(terminals)}
                        onValueSelected={terminal => {
                            onUpdatePoiFields({ terminal });
                        }}
                        disabled={true} // always disable building
                    />
                    <SelectFieldEditor
                        label="Level"
                        selectedValue={poi.floor}
                        creatable
                        options={makeSelectLabels(levels)}
                        onValueSelected={floor => {
                            onUpdatePoiFields({ floor });
                        }}
                        disabled={true} // always disable level
                    />
                    <TextFieldEditor
                        label="Nearby location"
                        value={poi.gate}
                        onChange={gate => {
                            onUpdatePoiFields({ gate });
                        }}
                        disabled={disabled}
                    />
                </div>

                {this.renderBanner('SECURITY RESTRICTIONS', [POI_FIELDS.SECURITY])}
                <div className={s.controlsGrid}>
                    <CheckboxEditor label="Restricted access required" value={isRestricted(poi)} onValueChanged={this.setSecurityRestricted} disabled={disabled} />
                </div>

                {this.renderBanner('DESCRIPTION', [POI_FIELDS.DESCRIPTION])}
                <TextAreaEditor
                    placeHolder="Enter a description..."
                    value={poi.description}
                    onChange={description => {
                        onUpdatePoiFields({ description });
                    }}
                    disabled={disabled}
                />

                {this.renderBanner('INFORMATION URL', [POI_FIELDS.URL], 'Use fully qualified URLs. This link will be displayed under the information section of the POI.')}
                <div className={s.controlsGrid}>
                    <div className={s.fullWidth}>
                        <TextFieldEditor
                            className={s.fullWidth}
                            value={poi.url}
                            label="URL"
                            placeHolder="https://www.example.com"
                            invalid={!!urlErrors.url}
                            onChange={url => {
                                onUpdatePoiFields({ url });
                            }}
                            disabled={disabled}
                        />
                        {urlErrors.url && (
                            <div className={s.errorText} data-testid="info-url-error">{urlErrors.url}</div>
                        )}
                    </div>
                </div>

                {poi.category?.toLowerCase().includes('parking') && (
                    <>
                        {this.renderBanner('RESERVE PARKING URL', [POI_FIELDS.PARKING_URL], 'Use fully qualified URLs. This link will be displayed as a "Reserve Parking" call to action button on the POI, directing users to a parking reservation page.')}
                        <div className={s.controlsGrid}>
                            <div className={s.fullWidth}>
                                <TextFieldEditor
                                    className={s.fullWidth}
                                    value={poi.parkingUrl}
                                    label="PARKING URL"
                                    placeHolder="https://www.example.com/parking"
                                    invalid={!!urlErrors.parkingUrl}
                                    onChange={parkingUrl => {
                                        onUpdatePoiFields({ parkingUrl });
                                    }}
                                    disabled={disabled}
                                />
                                {urlErrors.parkingUrl && (
                                    <div className={s.errorText} data-testid="parking-url-error">{urlErrors.parkingUrl}</div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {poi.category?.toLowerCase().includes('eat') && (
                    <>
                        {this.renderBanner('MOBILE ORDERING URL', [POI_FIELDS.MOBILE_ORDERING_URL], 'Use fully qualified URLs. This link will be displayed as a "Order/Pay Online" button on the POI.')}
                        <div className={s.controlsGrid}>
                            <div className={s.fullWidth}>
                                <TextFieldEditor
                                    className={s.fullWidth}
                                    value={poi.mobileOrderingUrl}
                                    label="URL"
                                    placeHolder="https://www.your-order-url.com"
                                    invalid={!!urlErrors.mobileOrderingUrl}
                                    onChange={mobileOrderingUrl => {
                                        onUpdatePoiFields({ mobileOrderingUrl });
                                    }}
                                    disabled={disabled}
                                />
                                {urlErrors.mobileOrderingUrl && (
                                    <div className={s.errorText} data-testid="mobile-ordering-url-error">{urlErrors.mobileOrderingUrl}</div>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {this.renderBanner('PHONE', [POI_FIELDS.PHONE, POI_FIELDS.PHONE_EXT], 'Include country code in phone numbers for international dialing support')}
                <div className={s.controlsGrid}>
                    <div>
                        <TextFieldEditor value={poi.phone} label="PHONE NUMBER" placeHolder="+1 (555) 555-5555" invalid={!!phoneError} onChange={this.onPhoneNumberChange} disabled={disabled} />
                        {phoneError && (
                            <div className={s.errorText} data-testid="phone-error">{phoneError}</div>
                        )}
                    </div>
                    <div>
                        <TextFieldEditor
                            value={poi.phoneExt}
                            label="PHONE EXTENSION"
                            placeHolder="217"
                            invalid={!!phoneExtError}
                            onChange={this.onPhoneExtensionChange}
                            disabled={disabled}
                        />
                        {phoneExtError && (
                            <div className={s.errorText} data-testid="phone-ext-error">{phoneExtError}</div>
                        )}
                    </div>
                </div>

                {this.renderBanner('OPERATING HOURS', [POI_FIELDS.RAW_HOURS], 'Select the day(s)')}
                <OSMHoursEditor
                    configuredLinesString={poi.rawHours}
                    onChange={rawHours => {
                        onUpdatePoiFields({ rawHours });
                    }}
                    disabled={disabled}
                />

                <div className={s.spacer} />

                {this.renderBanner('ADDITIONAL ATTRIBUTES', [POI_FIELDS.ADDITIONAL_ATTRIBUTES])}
                <TextAreaEditor
                    placeHolder="Add some attributes..."
                    value={poi.additionalAttributes}
                    onChange={additionalAttributes => {
                        onUpdatePoiFields({ additionalAttributes: normalizeAdditionalAttributes(additionalAttributes) });
                    }}
                    disabled={disabled}
                />
            </div>
        );
    };
}
